import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Stack,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,
  Typography,
  useMediaQuery,
  useTheme,
  Fade,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import { useAuth } from "../../context/authContext";
import { getDefaultEvaluationPeriod } from "../../utils/evaluationPeriod";
import techniciansService from "../../api/techniciansService";
import qualityService from "../../api/qualityService";

import EvaluationHeader from "./components/EvaluationHeader";
import RoleBanner from "./components/RoleBanner";
import EvaluationStats from "./components/EvaluationStats";
import TechnicianEvaluationTable from "./components/TechnicianEvaluationTable";
import TechnicianEvaluationCard from "./components/TechnicianEvaluationCard";
import AdminOverviewTable from "./components/AdminOverviewTable";
import EvaluationSkeleton, { EmptyState } from "./components/EvaluationSkeleton";
import {
  toScale,
  toBackend,
  CRITERIA,
  getEditableCriteria,
  isOverviewViewer,
  showsEvaluatorStamp,
} from "./constants";

const defaultPeriod = getDefaultEvaluationPeriod();

const EMPTY_ROW = {
  serviceQualityScore: 0,
  punctualityScore: 0,
  attitudeScore: 0,
  adminComplianceScore: 0,
  notes: "",
  evaluators: {},
};

export default function Evaluacion() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user, role } = useAuth();

  const [year, setYear] = useState(defaultPeriod.year);
  const [month, setMonth] = useState(defaultPeriod.month);
  const [search, setSearch] = useState("");

  const [technicians, setTechnicians] = useState([]);
  const [rows, setRows] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const isOverview = isOverviewViewer(role);
  const editableCriteria = useMemo(() => getEditableCriteria(role), [role]);

  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const [techRes, evalRes] = await Promise.all([
          techniciansService.getAll(),
          qualityService.getByPeriod(year, month),
        ]);
        if (!active) return;

        const techs = techRes.data.data || [];
        const evals = evalRes.data.data || [];

        const evalMap = {};
        evals.forEach((e) => {
          const id = e.technician?._id || e.technician;
          evalMap[id] = {
            serviceQualityScore: toScale(e.serviceQualityScore),
            punctualityScore: toScale(e.punctualityScore),
            attitudeScore: toScale(e.attitudeScore),
            adminComplianceScore: toScale(e.adminComplianceScore),
            notes: e.notes ?? "",
            evaluators: e.evaluators ?? {},
          };
        });

        const initialRows = {};
        techs.forEach((t) => {
          const fromEval = evalMap[t._id];
          initialRows[t._id] = fromEval
            ? {
                ...fromEval,
                attitudeScore: fromEval.attitudeScore || toScale(t.attitudeScore),
                adminComplianceScore: fromEval.adminComplianceScore || toScale(t.adminComplianceScore),
              }
            : {
                ...EMPTY_ROW,
                attitudeScore: toScale(t.attitudeScore),
                adminComplianceScore: toScale(t.adminComplianceScore),
              };
        });

        setTechnicians(techs);
        setRows(initialRows);
      } catch (err) {
        console.error("Error cargando evaluaciones", err);
        if (active) showSnackbar("Error al cargar datos", "error");
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [year, month]);

  const sortedTechnicians = useMemo(
    () =>
      [...technicians].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" })
      ),
    [technicians]
  );

  const filteredTechnicians = useMemo(() => {
    if (!search.trim()) return sortedTechnicians;
    const q = search.toLowerCase();
    return sortedTechnicians.filter(
      (t) =>
        t.name?.toLowerCase().includes(q) ||
        t.zone?.toLowerCase().includes(q)
    );
  }, [sortedTechnicians, search]);

  const stats = useMemo(() => {
    const total = sortedTechnicians.length;
    let evaluated = 0;
    let scoreSum = 0;
    let scoreCount = 0;

    const criteriaFields = isOverview
      ? CRITERIA.map((c) => c.field)
      : editableCriteria.map((c) => c.field);

    sortedTechnicians.forEach((t) => {
      const r = rows[t._id];
      if (!r) return;
      const hasEval = criteriaFields.some((f) => (r[f] ?? 0) > 0);
      if (hasEval) evaluated++;
      criteriaFields.forEach((f) => {
        if ((r[f] ?? 0) > 0) {
          scoreSum += r[f];
          scoreCount++;
        }
      });
    });

    const avgScore = scoreCount > 0 ? Math.round((scoreSum / scoreCount) * 10) / 10 : 0;
    return { total, evaluated, pending: total - evaluated, avgScore };
  }, [sortedTechnicians, rows, editableCriteria, isOverview]);

  const handleChange = (techId, field, value) => {
    setRows((prev) => ({
      ...prev,
      [techId]: { ...prev[techId], [field]: value },
    }));
  };

  const buildEvaluatorMeta = () => ({
    userId: user?._id || user?.id,
    userName: user?.name || user?.email || "Usuario",
    role,
    at: new Date().toISOString(),
  });

  const handleSave = async (techId) => {
    const row = rows[techId];
    setSavingId(techId);

    const evaluators = { ...(row.evaluators || {}) };
    const meta = buildEvaluatorMeta();

    editableCriteria.forEach((c) => {
      if (showsEvaluatorStamp(c.id)) {
        evaluators[c.evaluatorKey] = meta;
      }
    });

    const payload = {
      technician: techId,
      year,
      month,
      serviceQualityScore: toBackend(row.serviceQualityScore),
      punctualityScore: toBackend(row.punctualityScore),
      attitudeScore: toBackend(row.attitudeScore),
      adminComplianceScore: toBackend(row.adminComplianceScore),
      notes: row.notes || "",
      evaluators,
    };

    try {
      await qualityService.upsert(payload);

      if (editableCriteria.some((c) => c.id === "attitude")) {
        await techniciansService.update(techId, {
          attitudeScore: toBackend(row.attitudeScore),
        });
      }

      if (editableCriteria.some((c) => c.id === "adminCompliance")) {
        await techniciansService.update(techId, {
          adminComplianceScore: toBackend(row.adminComplianceScore),
        });
      }

      setRows((prev) => ({
        ...prev,
        [techId]: { ...prev[techId], evaluators },
      }));

      showSnackbar("Evaluación guardada", "success");
    } catch (err) {
      console.error("Error guardando evaluación", err);
      showSnackbar("No se pudo guardar", "error");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Box sx={{ bgcolor: "#f8f9fc", minHeight: "100%", width: "100%" }}>
      <Container maxWidth={false} disableGutters sx={{ width: "100%" }}>
        <Fade in timeout={400}>
          <Box
            sx={{
              bgcolor: "#fff",
              borderRadius: { xs: "16px", sm: "20px" },
              border: "1px solid #e8eaef",
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              p: { xs: 2.5, sm: 3, md: 4 },
            }}
          >
            <EvaluationHeader
              month={month}
              year={year}
              onMonthChange={setMonth}
              onYearChange={setYear}
            />

            {loading ? (
              <EvaluationSkeleton count={isMobile ? 3 : 5} />
            ) : (
              <>
                <RoleBanner user={user} role={role} />
                <EvaluationStats {...stats} />

                <Box sx={{ mb: 2.5 }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Buscar técnico…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 20, color: "#9ca3af" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        bgcolor: "#fafbfc",
                        fontSize: "0.875rem",
                        "& fieldset": { borderColor: "#e8eaef" },
                        "&:hover fieldset": { borderColor: "#d1d5db" },
                      },
                    }}
                  />
                </Box>

                {filteredTechnicians.length === 0 ? (
                  <EmptyState
                    message={
                      search
                        ? "No se encontraron técnicos con ese nombre."
                        : "No hay técnicos registrados."
                    }
                  />
                ) : isOverview ? (
                  <AdminOverviewTable
                    technicians={filteredTechnicians}
                    rows={rows}
                    role={role}
                    onChange={handleChange}
                    onSave={handleSave}
                    savingId={savingId}
                  />
                ) : isMobile ? (
                  <Stack spacing={1.5}>
                    {filteredTechnicians.map((tech) => (
                      <TechnicianEvaluationCard
                        key={tech._id}
                        tech={tech}
                        row={rows[tech._id] || {}}
                        criteria={editableCriteria}
                        onChange={handleChange}
                        onSave={handleSave}
                        saving={savingId === tech._id}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Box
                    sx={{
                      border: "1px solid #e8eaef",
                      borderRadius: "14px",
                      overflow: "hidden",
                    }}
                  >
                    <TechnicianEvaluationTable
                      technicians={filteredTechnicians}
                      rows={rows}
                      criteria={editableCriteria}
                      onChange={handleChange}
                      onSave={handleSave}
                      savingId={savingId}
                    />
                  </Box>
                )}

                {!isMobile && filteredTechnicians.length > 0 && (
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mt: 2, color: "#9ca3af", textAlign: "right" }}
                  >
                    {filteredTechnicians.length} técnico{filteredTechnicians.length !== 1 ? "s" : ""}
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Fade>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: "10px", fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
